import React, { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Bell,
  User,
  Eye,
  FileText,
  ChevronDown,
  Users,
  RefreshCw,
  Loader2,
  ArrowLeft,
  ArrowRight,
  MessageSquare,
  Flag,
} from "lucide-react";
import UserProfilePopup from "../layout/UserProfilePopup";
import PlayerRadarChart from "./PlayerRadarChart";
import PlayerCard from "./PlayerCard";
import ContactRequestModal from "./ContactRequestModal";
import ReportModal from "./ReportModal";
import { getFormattedDate } from "../../utils/formatDate";
import { formatHeight } from "../../utils/formatHeight";
import defaultAvatar from "../../utils/profile.png";
import { useAthlete } from "../../hooks/queries/useAthletes";
import {
  useAthleteCareer,
  useAthletePreferences,
  useLatestAthleteTracking,
} from "../../hooks/queries/useAthleteData";
import {
  useLatestScoutTracking,
  usePatchScoutTrackingDay,
} from "../../hooks/queries/useScouts";
import { useScoutContactRequestsMap } from "../../hooks/queries/useContactRequests";
import { useAcademyRoster } from "../../hooks/queries/useAcademies";
import {
  FOOTBALL_POSITIONS,
  athletePositionCode,
} from "../../constants/footballTaxonomy";
import { localDateKey } from "../../utils/localDate";
import { useAuth } from "../../context/AuthContext";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
} from "recharts";

// Referencia estable: un `new Set()` inline cambiaría de identidad en cada
// render y re-dispararía todo lo que dependa del roster.
const EMPTY_ROSTER = new Set();

const matchesMedia = (query) =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(query).matches;

const onNextFrame = (callback) => {
  if (
    typeof window !== "undefined" &&
    typeof window.requestAnimationFrame === "function"
  ) {
    window.requestAnimationFrame(callback);
    return;
  }
  callback();
};

const SORT_OPTIONS_BASE = [
  { value: "recent", label: "Más recientes" },
  { value: "points", label: "Mayor puntaje" },
  { value: "age_asc", label: "Más jóvenes" },
  { value: "name", label: "Nombre A-Z" },
];

const SORT_OPTIONS_WITH_MATCH = [
  { value: "match", label: "Mejor match" },
  ...SORT_OPTIONS_BASE,
];

const TIMEFRAME_OPTIONS = [
  { value: "daily", label: "Diario" },
  { value: "weekly", label: "Semanal" },
  { value: "monthly", label: "Mensual" },
];

const TIMEFRAME_DAY_WINDOWS = {
  daily: 1,
  weekly: 7,
  monthly: 30,
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Clave consistente para mapear un atleta de la lista contra el mapa de
// solicitudes de contacto (keyed por athlete_id).
const getPlayerAthleteKey = (player) => {
  const key = player?.athlete_id ?? player?.athleteId ?? player?.id;
  return key != null ? String(key) : null;
};

const CONTACT_STATUS_FILTERS = [
  { value: "all", label: "Estado de contacto" },
  { value: "none", label: "Sin contactar" },
  { value: "pending", label: "Solicitud enviada" },
  { value: "approved", label: "Conectados" },
  { value: "rejected", label: "Rechazadas" },
];

const normalizeFilterText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const uniqueOptions = (items, key) =>
  [
    ...new Set(
      items
        .map((item) => item?.[key])
        .filter((value) => String(value || "").trim()),
    ),
  ].sort((a, b) => String(a).localeCompare(String(b), "es"));

const formatDateKey = (dateInput) => {
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const RecruiterSummary = ({
  onNotificationsClick,
  onSearchClick,
  onOpenProfile,
  athletes = [],
  scoutProfile = null,
  scoutTrackingData = null,
  onOpenAthleteDetail,
  isAthleteDetailLoading = false,
  initialSelectedAthleteId = null,
}) => {
  const { signOut } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPosition, setFilterPosition] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterContactStatus, setFilterContactStatus] = useState("all");
  const hasMatchScores = useMemo(
    () => athletes.some((a) => typeof a?.score === "number" && a.score > 0),
    [athletes],
  );
  const [sortBy, setSortBy] = useState("recent");
  useEffect(() => {
    if (hasMatchScores) {
      setSortBy((prev) => (prev === "recent" ? "match" : prev));
    } else {
      setSortBy((prev) => (prev === "match" ? "recent" : prev));
    }
  }, [hasMatchScores]);
  const sortOptions = hasMatchScores ? SORT_OPTIONS_WITH_MATCH : SORT_OPTIONS_BASE;
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [timeframe, setTimeframe] = useState("weekly");
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const profileButtonRef = useRef(null);
  const mobileDetailHeadingRef = useRef(null);
  const playerCardRefs = useRef(new Map());
  const queryClient = useQueryClient();
  const navigateToMessages = useNavigate();

  useEffect(() => {
    if (!initialSelectedAthleteId || selectedPlayer) return;

    const restoredPlayer = athletes.find((athlete) => {
      const athleteKey = athlete?.athlete_id || athlete?.id;
      return String(athleteKey) === String(initialSelectedAthleteId);
    });

    if (restoredPlayer) {
      setSelectedPlayer(restoredPlayer);
    }
  }, [athletes, initialSelectedAthleteId, selectedPlayer]);

  const selectedAthleteIds = useMemo(() => {
    if (!selectedPlayer) return [];

    const candidates = [
      selectedPlayer?.athlete_id,
      selectedPlayer?.athleteId,
      selectedPlayer?.id,
      selectedPlayer?.user_id,
      selectedPlayer?.userId,
    ].filter(Boolean);

    return [...new Set(candidates.map((candidate) => String(candidate)))];
  }, [selectedPlayer]);

  const selectedAthleteId = selectedAthleteIds[0] || null;

  // Restore the saved timeframe once. Every change is saved and refetched, so
  // re-applying the server value on each refetch could snap a newer choice
  // back to an older one still in flight.
  const hasRestoredTimeframeRef = useRef(false);
  useEffect(() => {
    if (hasRestoredTimeframeRef.current) return;
    const savedTimeframe =
      scoutTrackingData?.metrics?.athlete_visibility?.timeframe;

    if (
      savedTimeframe &&
      TIMEFRAME_OPTIONS.some((option) => option.value === savedTimeframe)
    ) {
      hasRestoredTimeframeRef.current = true;
      setTimeframe(savedTimeframe);
    }
  }, [scoutTrackingData]);

  // Métricas de entrenamiento del atleta seleccionado (último registro).
  const latestAthleteTrackingQuery = useLatestAthleteTracking(selectedAthleteId);
  const trainingMetrics =
    latestAthleteTrackingQuery.data?.metrics?.training || null;
  const isTrainingLoading = latestAthleteTrackingQuery.isLoading;

  // Evaluación inicial: perfil + carrera + preferencias del atleta seleccionado.
  const athleteQuery = useAthlete(selectedAthleteId);
  const careerQuery = useAthleteCareer(selectedAthleteId);
  const preferencesQuery = useAthletePreferences(selectedAthleteId);
  const isEvaluationLoading =
    athleteQuery.isLoading ||
    careerQuery.isLoading ||
    preferencesQuery.isLoading;

  const evaluationData = useMemo(() => {
    if (!selectedAthleteId) return null;

    return {
      attributes: athleteQuery.data?.attributes || {},
      position: athleteQuery.data?.position || "",
      career: careerQuery.data || null,
      preferences: preferencesQuery.data || null,
    };
  }, [
    selectedAthleteId,
    athleteQuery.data,
    careerQuery.data,
    preferencesQuery.data,
  ]);

  // Hidrata el estado de las solicitudes de contacto de toda la lista en una
  // sola query, para saber qué etiqueta mostrar en "Ver perfil completo".
  // react-query refetchOnWindowFocus cubre el refresco cuando el atleta
  // responde en su propia sesión y el scout vuelve a enfocar la pestaña.
  const contactRequestsQuery = useScoutContactRequestsMap({
    enabled: Boolean(scoutProfile?.id),
  });
  const contactRequestsByAthleteId = useMemo(
    () => contactRequestsQuery.data || {},
    [contactRequestsQuery.data],
  );

  // Atletas confirmados en la academia del scout: a ésos se les abre el perfil
  // completo directo, sin solicitud de contacto. El backend resuelve la academia
  // desde la sesión, así que hidratamos toda la lista con una sola query.
  const academyRosterQuery = useAcademyRoster({
    enabled: Boolean(scoutProfile?.academy_id),
  });
  const academyAthleteIds = academyRosterQuery.data ?? EMPTY_ROSTER;

  // Se espera a que cargue scout_tracking para no guardar el periodo por
  // defecto antes de restaurar el guardado. El PATCH solo reemplaza
  // athlete_visibility; el resto de secciones del día se conserva en el
  // servidor.
  const scoutTrackingQuery = useLatestScoutTracking(scoutProfile?.id);
  const isScoutTrackingReady = scoutTrackingQuery.isSuccess;

  const { mutateAsync: patchScoutTrackingDay } = usePatchScoutTrackingDay();

  useEffect(() => {
    const persistVisibility = async () => {
      if (!scoutProfile?.id || !isScoutTrackingReady) return;

      const visibilityPayload = {
        timeframe,
        selected_athlete_id: selectedAthleteId || null,
        updated_at: new Date().toISOString(),
      };

      try {
        await patchScoutTrackingDay({
          date: localDateKey(),
          metrics: { athlete_visibility: visibilityPayload },
        });
      } catch (error) {
        console.warn(
          "No se pudo actualizar la visibilidad del scout:",
          error.message,
        );
      }
    };

    persistVisibility();
  }, [
    timeframe,
    selectedAthleteId,
    scoutProfile?.id,
    isScoutTrackingReady,
    patchScoutTrackingDay,
  ]);

  // ── Filtrado y ordenamiento sobre datos reales ────────────────
  const filteredPlayers = useMemo(() => {
    let list = [...athletes];

    // Búsqueda por nombre
    if (searchTerm.trim()) {
      const q = normalizeFilterText(searchTerm);
      list = list.filter((a) => normalizeFilterText(a.full_name).includes(q));
    }

    // Filtro posición: codes de la taxonomía de fútbol (el texto libre legado
    // se mapea a su code). No hay filtro de deporte: solo fútbol.
    if (filterPosition !== "all") {
      list = list.filter((a) => athletePositionCode(a) === filterPosition);
    }

    // Filtro categoría
    if (filterCategory !== "all") {
      list = list.filter(
        (a) =>
          normalizeFilterText(a.category) === normalizeFilterText(filterCategory),
      );
    }

    // Filtro por estado de la solicitud de contacto
    if (filterContactStatus !== "all") {
      list = list.filter((a) => {
        const key = getPlayerAthleteKey(a);
        const status = key ? contactRequestsByAthleteId[key]?.status : null;
        if (filterContactStatus === "none") return !status;
        return status === filterContactStatus;
      });
    }

    // Ordenamiento
    list.sort((a, b) => {
      if (sortBy === "match") {
        const sa = typeof a.score === "number" ? a.score : -1;
        const sb = typeof b.score === "number" ? b.score : -1;
        return sb - sa;
      }
      if (sortBy === "recent")
        return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === "points") return (b.points || 0) - (a.points || 0);
      if (sortBy === "age_asc") return (a.age || 99) - (b.age || 99);
      if (sortBy === "name")
        return (a.full_name || "").localeCompare(b.full_name || "");
      return 0;
    });

    return list;
  }, [
    athletes,
    searchTerm,
    filterPosition,
    filterCategory,
    filterContactStatus,
    contactRequestsByAthleteId,
    sortBy,
  ]);

  const resetFilters = () => {
    setSearchTerm("");
    setFilterPosition("all");
    setFilterCategory("all");
    setFilterContactStatus("all");
    setSortBy("recent");
    setSelectedPlayer(null);
  };

  const hasActiveFilters =
    searchTerm ||
    filterPosition !== "all" ||
    filterCategory !== "all" ||
    filterContactStatus !== "all";

  const firstName = scoutProfile?.full_name?.split(" ")[0] || "Scout";
  const categoryOptions = useMemo(
    () => uniqueOptions(athletes, "category"),
    [athletes],
  );

  const trainingChartData = useMemo(() => {
    const bySport = trainingMetrics?.bySport;
    if (!bySport || typeof bySport !== "object") return [];

    const sportKeys = Object.keys(bySport);
    if (sportKeys.length === 0) return [];

    const preferredSport =
      trainingMetrics?.lastSport && bySport[trainingMetrics.lastSport]
        ? trainingMetrics.lastSport
        : sportKeys[0];

    const sportData = bySport[preferredSport] || {};
    const fields =
      sportData?.fields && typeof sportData.fields === "object"
        ? sportData.fields
        : {};

    const perDayMetricLatest = new Map();

    Object.entries(fields).forEach(([fieldKey, field]) => {
      const fieldLabel = field?.title || fieldKey;
      const history = Array.isArray(field?.history) ? field.history : [];

      history.forEach((entry) => {
        const numericValue = toNumber(entry?.value);
        const recordedAt = entry?.recordedAt;
        const timestamp = recordedAt ? new Date(recordedAt).getTime() : NaN;
        const dayKey = formatDateKey(recordedAt);

        if (numericValue === null || Number.isNaN(timestamp) || !dayKey) return;

        const uniqueMetricPerDayKey = `${dayKey}::${fieldLabel}`;
        const current = perDayMetricLatest.get(uniqueMetricPerDayKey);

        if (!current || timestamp > current.timestamp) {
          perDayMetricLatest.set(uniqueMetricPerDayKey, {
            dayKey,
            date: new Date(recordedAt),
            value: numericValue,
            metric: fieldLabel,
            timestamp,
          });
        }
      });

      if (history.length === 0) {
        const latestValue = toNumber(field?.latest?.value);
        const latestAt =
          field?.latest?.recordedAt || trainingMetrics?.lastUpdatedAt;
        const latestTimestamp = latestAt ? new Date(latestAt).getTime() : NaN;
        const dayKey = formatDateKey(latestAt);

        if (latestValue !== null && !Number.isNaN(latestTimestamp) && dayKey) {
          const uniqueMetricPerDayKey = `${dayKey}::${fieldLabel}`;
          const current = perDayMetricLatest.get(uniqueMetricPerDayKey);

          if (!current || latestTimestamp > current.timestamp) {
            perDayMetricLatest.set(uniqueMetricPerDayKey, {
              dayKey,
              date: new Date(latestAt),
              value: latestValue,
              metric: fieldLabel,
              timestamp: latestTimestamp,
            });
          }
        }
      }
    });

    const groupedByDay = new Map();

    Array.from(perDayMetricLatest.values()).forEach((entry) => {
      const existing = groupedByDay.get(entry.dayKey) || {
        date: entry.date,
        sum: 0,
        count: 0,
        metrics: [],
      };

      groupedByDay.set(entry.dayKey, {
        date: existing.date > entry.date ? existing.date : entry.date,
        sum: existing.sum + entry.value,
        count: existing.count + 1,
        metrics: [...existing.metrics, entry.metric],
      });
    });

    const records = Array.from(groupedByDay.entries()).map(
      ([dayKey, entry]) => ({
        dayKey,
        date: entry.date,
        value:
          entry.count > 0 ? Number((entry.sum / entry.count).toFixed(2)) : 0,
        metricCount: entry.count,
        metrics: entry.metrics,
      }),
    );

    if (records.length === 0) return [];

    records.sort((a, b) => a.date.getTime() - b.date.getTime());

    const windowDays = TIMEFRAME_DAY_WINDOWS[timeframe] || 7;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - windowDays);

    const filteredRecords = records.filter((entry) => entry.date >= cutoff);
    const finalRecords = filteredRecords.length > 0 ? filteredRecords : records;

    return finalRecords.map((entry) => ({
      dateLabel: entry.date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
      }),
      value: entry.value,
      legend: `${preferredSport} · Promedio diario (${entry.metricCount || 0} ejercicios)`,
      metricCount: entry.metricCount || 0,
      isoDate: entry.date.toISOString(),
    }));
  }, [trainingMetrics, timeframe]);

  const radarStats = useMemo(() => {
    const evaluationAttributes = evaluationData?.attributes || {};
    const speed = toNumber(evaluationAttributes?.speed) ?? 0;
    const endurance = toNumber(evaluationAttributes?.endurance) ?? 0;
    const pace = Math.max(0, Math.min(100, speed));
    const physical = Math.max(0, Math.min(100, endurance));

    return {
      pace,
      shooting: physical,
      passing: pace,
      dribbling: pace,
      defending: physical,
      physical,
    };
  }, [evaluationData?.attributes]);

  const hasEvaluationRadar = radarStats.pace > 0 || radarStats.physical > 0;

  const latestExerciseSummary = useMemo(() => {
    const bySport = trainingMetrics?.bySport;
    if (!bySport || typeof bySport !== "object") return [];

    const sportKeys = Object.keys(bySport);
    if (sportKeys.length === 0) return [];

    const preferredSport =
      trainingMetrics?.lastSport && bySport[trainingMetrics.lastSport]
        ? trainingMetrics.lastSport
        : sportKeys[0];

    const fields = bySport?.[preferredSport]?.fields || {};

    return Object.entries(fields)
      .map(([fieldKey, field]) => {
        const value = toNumber(field?.latest?.value);
        if (value === null) return null;

        return {
          key: fieldKey,
          label: field?.title || fieldKey,
          value,
          unit: field?.unit || "",
        };
      })
      .filter(Boolean)
      .slice(0, 6);
  }, [trainingMetrics]);

  const latestTrainingPoint =
    trainingChartData.length > 0
      ? trainingChartData[trainingChartData.length - 1]
      : null;

  // Última solicitud de contacto del atleta seleccionado (si existe).
  const selectedContactRequest = selectedAthleteId
    ? contactRequestsByAthleteId[selectedAthleteId] || null
    : null;
  const contactStatus = selectedContactRequest?.status || null;
  const isContactPending = contactStatus === "pending" || contactStatus === "guardian_pending";

  // ¿El atleta seleccionado está confirmado en la academia del scout? En ese
  // caso el acceso es directo: la academia ya respondió por él, así que pedirle
  // permiso a su propio atleta no tiene sentido.
  const isSameAcademyMember = selectedAthleteIds.some((id) =>
    academyAthleteIds.has(String(id)),
  );

  // Gate: acceso directo si es de la misma academia o si el atleta aprobó la
  // solicitud. Si no existe solicitud o fue rechazada, abrimos el modal. Si está
  // pendiente, no-op.
  const handleViewFullProfile = () => {
    if (isSameAcademyMember || contactStatus === "approved") {
      onOpenAthleteDetail?.(selectedPlayer);
      return;
    }
    if (isContactPending) return;
    setIsContactModalOpen(true);
  };

  // Tras enviar la solicitud, actualizamos la caché local sin esperar el
  // refetch (la mutación ya invalida ["contact-requests"] por detrás).
  const handleContactSubmitted = (newRow) => {
    if (!newRow?.athlete_id) return;
    queryClient.setQueryData(
      ["contact-requests", "by-athlete-map"],
      (prev) => ({
        ...(prev || {}),
        [newRow.athlete_id]: newRow,
      }),
    );
  };

  const handlePlayerSelection = (player) => {
    const playerKey = player?.id ?? player?.athlete_id;
    const selectedKey = selectedPlayer?.id ?? selectedPlayer?.athlete_id;
    setSelectedPlayer(selectedKey === playerKey ? null : player);
  };

  const handleBackToPlayerList = () => {
    const selectedKey = selectedPlayer?.id ?? selectedPlayer?.athlete_id;
    setSelectedPlayer(null);

    onNextFrame(() => {
      playerCardRefs.current.get(String(selectedKey))?.focus();
    });
  };

  useEffect(() => {
    if (!selectedPlayer || !matchesMedia("(max-width: 1023px)")) return;

    onNextFrame(() => {
      mobileDetailHeadingRef.current?.focus({ preventScroll: true });
      mobileDetailHeadingRef.current?.scrollIntoView?.({
        behavior: matchesMedia("(prefers-reduced-motion: reduce)")
          ? "auto"
          : "smooth",
        block: "start",
      });
    });
  }, [selectedPlayer]);

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 relative">
      <div className="p-4 space-y-6 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-gray-500 text-sm font-medium">
              {getFormattedDate()}
            </p>
            <h1 className="text-2xl font-bold text-gray-900">
              Bienvenido de vuelta, {firstName}
            </h1>
            <p className="text-gray-500 text-sm">
              {athletes.length} deportistas registrados
            </p>
          </div>
          <div className="hidden items-center gap-3 relative z-10 lg:flex">
            <button
              type="button"
              onClick={onSearchClick}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Buscar atletas"
            >
              <Search size={20} className="text-gray-600" />
            </button>
            <button
              onClick={onNotificationsClick}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Bell size={20} className="text-gray-600" />
            </button>
            <button
              ref={profileButtonRef}
              onClick={() => setIsProfilePopupOpen(!isProfilePopupOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <User size={20} className="text-gray-600" />
            </button>
            <UserProfilePopup
              isOpen={isProfilePopupOpen}
              onClose={() => setIsProfilePopupOpen(false)}
              buttonRef={profileButtonRef}
              userData={scoutProfile}
              onLogout={signOut}
              onViewProfile={() => onOpenProfile?.()}
            />
          </div>
        </div>

        {/* Resumen rápido */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-5">
              Análisis de Talentos
            </h3>
            <div className="flex items-center justify-around border-t border-b border-gray-100 py-5">
              <div className="flex flex-col items-center gap-1 flex-1 border-r border-gray-100">
                <Eye className="text-orange-500" size={22} />
                <div className="text-2xl font-bold text-gray-900">
                  {athletes.length}
                </div>
                <div className="text-xs text-gray-500">Atletas</div>
              </div>
              <div className="flex flex-col items-center gap-1 flex-1 border-r border-gray-100">
                <FileText className="text-blue-500" size={22} />
                <div className="text-2xl font-bold text-gray-900">
                  {athletes.filter((a) => a.profile_complete).length}
                </div>
                <div className="text-xs text-gray-500">Perfil completo</div>
              </div>
              <div className="flex flex-col items-center gap-1 flex-1">
                <Users className="text-green-500" size={22} />
                <div className="text-2xl font-bold text-gray-900">
                  {
                    [...new Set(athletes.map((a) => a.sport).filter(Boolean))]
                      .length
                  }
                </div>
                <div className="text-xs text-gray-500">Deportes</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Deportes contactados
            </h3>
            <div className="space-y-2">
              {[...new Set(athletes.map((a) => a.sport).filter(Boolean))]
                .slice(0, 5)
                .map((sport) => {
                  const count = athletes.filter(
                    (a) => a.sport === sport,
                  ).length;
                  return (
                    <div
                      key={sport}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-700">{sport}</span>
                      <span className="font-semibold text-blue-600">
                        {count}
                      </span>
                    </div>
                  );
                })}
              {athletes.filter((a) => !a.sport).length > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400 italic">Sin deporte</span>
                  <span className="text-gray-400">
                    {athletes.filter((a) => !a.sport).length}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mis Talentos */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold text-gray-900">
              <span className={selectedPlayer ? "hidden lg:inline" : undefined}>
                Lista de Atletas
              </span>
              {selectedPlayer && (
                <span className="lg:hidden">Detalle del atleta</span>
              )}
            </h2>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className={`min-h-11 items-center gap-1.5 rounded-lg px-2 text-xs text-blue-600 transition-colors hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 lg:flex lg:min-h-0 lg:px-0 ${
                  selectedPlayer ? "hidden" : "flex"
                }`}
              >
                <RefreshCw size={13} />
                Limpiar filtros
              </button>
            )}
          </div>

          {/* Filtros */}
          <div
            className={`flex-wrap items-center gap-3 mb-6 ${
              selectedPlayer ? "hidden lg:flex" : "flex"
            }`}
          >
            <div className="relative flex-1 min-w-[220px]">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="min-h-11 w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {[
              {
                value: filterPosition,
                onChange: setFilterPosition,
                options: [
                  ["all", "Todas las posiciones"],
                  ...FOOTBALL_POSITIONS.map((p) => [p.code, p.label]),
                ],
              },
              {
                value: filterCategory,
                onChange: setFilterCategory,
                options: [
                  ["all", "Categoría"],
                  ...categoryOptions.map((category) => [category, category]),
                ],
              },
              {
                value: filterContactStatus,
                onChange: setFilterContactStatus,
                options: CONTACT_STATUS_FILTERS.map((o) => [o.value, o.label]),
              },
              {
                value: sortBy,
                onChange: setSortBy,
                options: sortOptions.map((o) => [o.value, o.label]),
              },
            ].map((sel, i) => (
              <div key={i} className="relative">
                <select
                  value={sel.value}
                  onChange={(e) => sel.onChange(e.target.value)}
                  className="min-h-11 appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {sel.options.map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  size={14}
                />
              </div>
            ))}
          </div>

          {/* Resultados count */}
          <p
            className={`text-xs text-gray-400 mb-4 ${
              selectedPlayer ? "hidden lg:block" : "block"
            }`}
          >
            {filteredPlayers.length} resultado
            {filteredPlayers.length !== 1 ? "s" : ""}
            {hasActiveFilters ? " con los filtros aplicados" : ""}
          </p>

          {/* Lista + Detalle */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lista */}
            <section
              className={`lg:col-span-1 space-y-2 max-h-[360px] overflow-y-auto pr-1 lg:block lg:max-h-[600px] ${
                selectedPlayer ? "hidden" : "block"
              }`}
              aria-label="Atletas disponibles"
            >
              {filteredPlayers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Users size={36} className="text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium">
                    {hasActiveFilters
                      ? "No hay deportistas con esos filtros"
                      : "No hay deportistas contactados"}
                  </p>
                  {hasActiveFilters && (
                    <button
                      onClick={resetFilters}
                      className="mt-3 text-sm text-blue-500 hover:underline"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
              ) : (
                filteredPlayers.map((player) => {
                  const playerKey = player.id ?? player.athlete_id;
                  const selectedKey =
                    selectedPlayer?.id ?? selectedPlayer?.athlete_id;
                  const athleteKey = getPlayerAthleteKey(player);
                  const cardContactStatus = athleteKey
                    ? contactRequestsByAthleteId[athleteKey]?.status || null
                    : null;

                  return (
                    <PlayerCard
                      key={playerKey}
                      ref={(node) => {
                        const refKey = String(playerKey);
                        if (node) playerCardRefs.current.set(refKey, node);
                        else playerCardRefs.current.delete(refKey);
                      }}
                      player={player}
                      isSelected={selectedKey === playerKey}
                      contactStatus={cardContactStatus}
                      controlsId="selected-athlete-detail"
                      onClick={() => handlePlayerSelection(player)}
                    />
                  );
                })
              )}
            </section>

            {/* Panel de detalle / radar */}
            <div
              id="selected-athlete-detail"
              className={`lg:col-span-2 lg:block ${
                selectedPlayer ? "block" : "hidden"
              }`}
            >
              {selectedPlayer ? (
                <div className="bg-gray-50 rounded-xl p-4 h-full min-h-[400px] sm:p-6">
                  <button
                    type="button"
                    onClick={handleBackToPlayerList}
                    className="mb-5 inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-semibold text-blue-800 transition-colors hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 lg:hidden"
                  >
                    <ArrowLeft size={18} aria-hidden="true" />
                    Volver a la lista
                  </button>

                  {/* Info del atleta seleccionado */}
                  <div className="flex items-start gap-4 mb-6">
                    <img
                      src={selectedPlayer.avatar_url || defaultAvatar}
                      alt={selectedPlayer.full_name}
                      onError={(e) => {
                        e.currentTarget.src = defaultAvatar;
                      }}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white shadow"
                    />
                    <div>
                      <h3
                        ref={mobileDetailHeadingRef}
                        tabIndex={-1}
                        className="text-xl font-bold text-gray-900 focus:outline-none"
                      >
                        {selectedPlayer.full_name || "Sin nombre"}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {[selectedPlayer.sport, selectedPlayer.position]
                          .filter(Boolean)
                          .join(" · ") || "Sin información"}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          {selectedPlayer.category || "Amateur"}
                        </span>
                        {selectedPlayer.country && (
                          <span className="text-xs text-gray-400">
                            {selectedPlayer.country}
                          </span>
                        )}
                        {selectedPlayer.profile_complete && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            Perfil completo
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-5 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleViewFullProfile}
                      disabled={
                        isAthleteDetailLoading ||
                        (isContactPending && !isSameAcademyMember)
                      }
                      title={
                        isSameAcademyMember
                          ? "Atleta de tu academia: acceso directo"
                          : isContactPending
                            ? "Esperando la respuesta del atleta"
                            : undefined
                      }
                      className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-blue-950 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isAthleteDetailLoading ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <ArrowRight size={14} />
                      )}
                      {isAthleteDetailLoading
                        ? "Abriendo..."
                        : isSameAcademyMember
                          ? "Ver perfil completo"
                          : isContactPending
                            ? "Solicitud enviada"
                            : "Ver perfil completo"}
                    </button>
                    {contactStatus === "approved" && selectedContactRequest?.id && (
                      <button
                        type="button"
                        onClick={() => navigateToMessages(`/scout/messages/${selectedContactRequest.id}`)}
                        className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-700 transition-colors hover:bg-blue-100"
                      >
                        <MessageSquare size={14} />
                        Mensaje
                      </button>
                    )}
                    {isSameAcademyMember && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-athletain-green/10 px-3 py-1 text-xs font-semibold text-athletain-green">
                        Atleta de tu academia
                      </span>
                    )}
                    {!isSameAcademyMember && contactStatus === "pending" && (
                      <p className="text-xs text-gray-500">
                        Te avisaremos cuando el atleta responda.
                      </p>
                    )}
                    {!isSameAcademyMember && contactStatus === "guardian_pending" && (
                      <p className="text-xs text-gray-500">
                        Esperando aprobación del tutor legal.
                      </p>
                    )}
                    {!isSameAcademyMember && contactStatus === "rejected" && (
                      <p className="text-xs text-gray-500">
                        Solicitud anterior rechazada. Puedes enviar una nueva.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsReportOpen(true)}
                      className="inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-red-600"
                      title="Reportar usuario"
                    >
                      <Flag size={14} />
                      Reportar
                    </button>
                  </div>

                  {/* Stats rápidas */}
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    {[
                      {
                        label: "Edad",
                        value:
                          selectedPlayer.age > 0
                            ? `${selectedPlayer.age} años`
                            : "—",
                      },
                      {
                        label: "Altura",
                        value: formatHeight(selectedPlayer.height),
                      },
                      {
                        label: "Puntos",
                        value:
                          selectedPlayer.points > 0
                            ? selectedPlayer.points
                            : "—",
                      },
                    ].map((stat) => (
                      <div
                        key={stat.label}
                        className="bg-white rounded-lg p-3 text-center shadow-sm"
                      >
                        <div className="font-bold text-gray-900">
                          {stat.value}
                        </div>
                        <div className="text-xs text-gray-400">
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Radar placeholder */}
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <h4 className="font-semibold text-gray-700 mb-3 text-sm">
                      Diagrama de facultades
                    </h4>
                    <div className="h-[280px] flex items-center justify-center">
                      {isEvaluationLoading ? (
                        <p className="text-sm text-gray-400 text-center">
                          Cargando evaluación inicial...
                        </p>
                      ) : selectedPlayer.stats || hasEvaluationRadar ? (
                        <PlayerRadarChart
                          stats={selectedPlayer.stats || radarStats}
                        />
                      ) : (
                        <p className="text-sm text-gray-400 text-center">
                          Las métricas detalladas estarán disponibles
                          <br />
                          cuando el deportista complete su evaluación.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-4 shadow-sm mt-4">
                    <h4 className="font-semibold text-gray-700 mb-3 text-sm">
                      Evaluación inicial
                    </h4>
                    {isEvaluationLoading ? (
                      <p className="text-sm text-gray-400">
                        Cargando evaluación...
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500">Club actual</p>
                          <p className="font-semibold text-gray-800">
                            {evaluationData?.career?.current_club || "—"}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500">
                            Nivel competitivo
                          </p>
                          <p className="font-semibold text-gray-800">
                            {evaluationData?.career?.competition_level || "—"}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500">
                            Objetivo principal
                          </p>
                          <p className="font-semibold text-gray-800">
                            {evaluationData?.preferences?.main_objective || "—"}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500">Agencia</p>
                          <p className="font-semibold text-gray-800">
                            {evaluationData?.preferences?.agency_status || "—"}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-xl p-4 shadow-sm mt-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <h4 className="font-semibold text-gray-700 text-sm">
                        Progreso de entrenamiento
                      </h4>
                      <div className="flex items-center bg-gray-100 rounded-lg p-1">
                        {TIMEFRAME_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setTimeframe(option.value)}
                            className={`min-h-11 px-3 py-1 text-xs rounded-md transition-colors ${
                              timeframe === option.value
                                ? "bg-white text-blue-600 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {isTrainingLoading ? (
                      <div className="h-[220px] flex items-center justify-center text-sm text-gray-500">
                        Cargando historial de entrenamiento...
                      </div>
                    ) : trainingChartData.length > 0 ? (
                      <>
                        <div className="h-[220px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trainingChartData}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e5e7eb"
                              />
                              <XAxis
                                dataKey="dateLabel"
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip
                                formatter={(value) => [value, "Valor"]}
                                labelFormatter={(_, payload) =>
                                  payload?.[0]?.payload?.legend ||
                                  "Entrenamiento"
                                }
                              />
                              <Line
                                type="linear"
                                dataKey="value"
                                stroke="#3b82f6"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="mt-4 text-xs text-gray-500">
                          Último registro:{" "}
                          {latestTrainingPoint?.dateLabel || "—"} ·{" "}
                          <span className="font-semibold text-gray-700">
                            {latestTrainingPoint?.value ?? "—"}
                          </span>
                        </div>
                        {latestExerciseSummary.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {latestExerciseSummary.map((item) => (
                              <span
                                key={item.key}
                                className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full"
                              >
                                {item.label}: {item.value}
                                {item.unit ? ` ${item.unit}` : ""}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="h-[220px] flex items-center justify-center text-sm text-gray-400 text-center">
                        No hay registros de entrenamiento en el período
                        seleccionado.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl h-full min-h-[400px] hidden flex-col items-center justify-center text-center p-8 border-2 border-dashed border-gray-200 lg:flex">
                  <Users size={40} className="text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium">
                    Selecciona un deportista
                  </p>
                  <p className="text-gray-400 text-sm mt-1">
                    Haz clic en una tarjeta para ver su perfil detallado
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ContactRequestModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        athlete={selectedPlayer}
        onSubmitted={handleContactSubmitted}
      />
      <ReportModal
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        reportedAthleteId={selectedAthleteId}
        reportedScoutId={null}
        reportedName={selectedPlayer?.full_name || ""}
        context="recruiter_summary"
      />
    </div>
  );
};

export default RecruiterSummary;
